import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { useCallback } from 'react'
import {
  GetWorkspaceApiKeysParams,
  GetWorkspaceMembersParams,
  workspaceApi
} from '@/api/workspaceApi'
import { UNLIMITED_USAGE } from '@/components/subscriptions/usageConfig'
import { useWorkspace } from '@/components/workspaces/WorkspaceContext'
import { FeatureKey, FeatureUsageSummary } from '@/types/subscription'
import { roundToDecimals } from '@/utils/file'

export const workspaceQueryKeys = {
  all: ['workspaces'] as const,
  usage: (workspaceId: string) =>
    [...workspaceQueryKeys.all, 'usage', workspaceId] as const,
  members: (workspaceId: string, params?: GetWorkspaceMembersParams) =>
    [...workspaceQueryKeys.all, 'members', workspaceId, params] as const,
  apiKeys: (workspaceId: string, params?: GetWorkspaceApiKeysParams) =>
    [...workspaceQueryKeys.all, 'apiKeys', workspaceId, params] as const
}

export const useGetWorkspaceUsage = () => {
  const { workspace } = useWorkspace()

  return useQuery({
    queryKey: workspaceQueryKeys.usage(workspace.id),
    queryFn: () => workspaceApi.getWorkspaceUsage(),
    select: (data) => {
      const keys = Object.keys(data) as FeatureKey[]

      return keys.reduce((acc, key) => {
        const remaining = Math.max(
          // round to 8 decimals to avoid floating point precision issues
          roundToDecimals(data[key].limit - data[key].used, 8),
          0
        )

        acc[key] = {
          ...data[key],
          remaining,
          isUnlimited: data[key].limit === UNLIMITED_USAGE
        }
        return acc
      }, {} as FeatureUsageSummary)
    }
  })
}

export const useInvalidateWorkspaceUsage = () => {
  const queryClient = useQueryClient()

  const invalidateWorkspaceUsage = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [...workspaceQueryKeys.all, 'usage']
    })
  }, [queryClient])

  return { invalidateWorkspaceUsage }
}

export const useCreateWorkspace = () => {
  return useMutation({
    mutationFn: workspaceApi.createWorkspace
  })
}

export const useUpdateWorkspace = () => {
  return useMutation({
    mutationFn: workspaceApi.updateWorkspace
  })
}

export const useGetWorkspaceMembers = (
  workspaceId: string,
  params?: GetWorkspaceMembersParams,
  options?: { keepPreviousData?: boolean; enabled?: boolean }
) => {
  return useQuery({
    queryKey: workspaceQueryKeys.members(workspaceId, params),
    queryFn: () => workspaceApi.getWorkspaceMembers(workspaceId, params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
    enabled: options?.enabled
  })
}

export const useInviteMembers = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: workspaceApi.inviteMembers,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...workspaceQueryKeys.all, 'members']
      })

      void queryClient.invalidateQueries({
        queryKey: [...workspaceQueryKeys.all, 'usage']
      })
    }
  })
}

export const useUpdateWorkspaceMember = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: workspaceApi.updateWorkspaceMember,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...workspaceQueryKeys.all, 'members']
      })
    }
  })
}

export const useResendMembershipInvite = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: workspaceApi.resendInvite,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...workspaceQueryKeys.all, 'members']
      })

      void queryClient.invalidateQueries({
        queryKey: [...workspaceQueryKeys.all, 'usage']
      })
    }
  })
}

export const useDeactivateWorkspaceMember = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: workspaceApi.deactivateWorkspaceMember,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...workspaceQueryKeys.all, 'members']
      })

      void queryClient.invalidateQueries({
        queryKey: [...workspaceQueryKeys.all, 'usage']
      })
    }
  })
}

export const useAcceptWorkspaceInvitation = () => {
  return useMutation({
    mutationFn: workspaceApi.acceptWorkspaceInvitation
  })
}

export const useSelectWorkspace = () => {
  return useMutation({
    mutationFn: workspaceApi.selectWorkspace
  })
}

export const useGetWorkspaceApiKeys = (
  workspaceId: string,
  params?: GetWorkspaceApiKeysParams,
  options?: { keepPreviousData?: boolean; enabled?: boolean }
) => {
  return useQuery({
    queryKey: workspaceQueryKeys.apiKeys(workspaceId, params),
    queryFn: () => workspaceApi.getWorkspaceApiKeys(params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
    enabled: options?.enabled
  })
}

export const useInvalidateWorkspaceApiKeys = () => {
  const queryClient = useQueryClient()

  const invalidateWorkspaceApiKeys = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [...workspaceQueryKeys.all, 'apiKeys']
    })
  }, [queryClient])

  return { invalidateWorkspaceApiKeys }
}

export const useCreateApiKey = () => {
  return useMutation({
    mutationFn: workspaceApi.createApiKey
  })
}

export const useDeleteApiKey = () => {
  return useMutation({
    mutationFn: workspaceApi.deleteApiKey
  })
}
